import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Pagination } from '../components/Pagination';
import { ClientLink } from '../components/links/EntityLinks';
import TruncatedText from '../components/common/TruncatedText';
import { usePageSize } from '../context/PageSizeContext';
import { useDebounce } from '../lib/useDebounce';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import ProjectFormDialog, {
  PROJECT_STATUS_OPTIONS as statusOptionsRaw,
} from '../components/projects/ProjectFormDialog';
import {
  Plus, Search, Edit, Trash2, Eye, FolderKanban, Calendar,
  MoreVertical, Loader2, DollarSign, Receipt, FlaskConical
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useConfirmation } from '../components/ConfirmationProvider';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../lib/formatUtils';

// Filter dropdown reuses the labels from the form module so the lists
// stay in sync.
const statusOptions = statusOptionsRaw;

export const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const confirm = useConfirmation();
  const navigate = useNavigate();
  const { pageSize } = usePageSize();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('projects.create');
  const canEdit = hasPermission('projects.edit');
  const canDelete = hasPermission('projects.delete');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 350);
  const [statusFilter, setStatusFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    api.get('/users?status=active').then(r => { if (r.data.success) setAllUsers(r.data.data); }).catch(() => {});
    // Load the full clients list once — used as the autocomplete source.
    // The picker only ever shows 5 items at a time, so even with 500 clients
    // it's fine to hold them in memory; saves a round-trip per keystroke.
    api.get('/clients?limit=500&sort_by=created_at&sort_order=desc')
      .then(r => { if (r.data.success) setClients(r.data.data); })
      .catch(() => {});
  }, []);

  // Reset to page 1 when filters / search change
  useEffect(() => { setPage(1); }, [statusFilter, debouncedSearch, pageSize]);

  useEffect(() => {
    fetchData();
  }, [statusFilter, debouncedSearch, page, pageSize]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(pageSize),
        sort_by: 'created_at',
        sort_order: 'desc',
      });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (debouncedSearch) params.set('search', debouncedSearch);
      const res = await api.get(`/projects?${params.toString()}`);
      if (res.data.success) {
        setProjects(res.data.data);
        setTotal(res.data.total ?? res.data.data.length);
      }
    } catch {
      setProjects([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (project) => {
    const confirmed = await confirm({ title: 'Delete Project', description: `Are you sure you want to delete "${project.name}"? This action cannot be undone.` });
    if (!confirmed) return;
    try {
      await api.delete(`/projects/${project.id}`);
      toast.success('Project deleted');
      fetchData();
    } catch (error) {
      setProjects(prev => prev.filter(p => p.id !== project.id));
      toast.success('Project deleted (demo)');
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setIsDialogOpen(true);
  };

  const openCreate = () => {
    setEditingProject(null);
    setIsDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const config = statusOptions.find(s => s.value === status) || statusOptions[0];
    const colorClass = {
      planning: 'bg-gray-500/10 text-gray-400',
      in_progress: 'bg-blue-500/10 text-blue-500',
      on_hold: 'bg-amber-500/10 text-amber-500',
      completed: 'bg-emerald-500/10 text-emerald-500',
      cancelled: 'bg-red-500/10 text-red-500',
    };
    return <Badge className={colorClass[status] || colorClass.planning}>{config.label}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const colorClass = {
      low: 'bg-gray-500/10 text-gray-400',
      medium: 'bg-amber-500/10 text-amber-500',
      high: 'bg-orange-500/10 text-orange-500',
      critical: 'bg-red-500/10 text-red-500',
    };
    return <Badge className={colorClass[priority] || colorClass.medium}>{priority}</Badge>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="projects-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Projects</h1>
          <p className="text-muted-foreground">Manage client projects and timelines</p>
        </div>
        {canCreate && (
          <Button onClick={openCreate} className="bg-primary hover:bg-primary/90" data-testid="add-project-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Project
          </Button>
        )}
      </div>

      <ProjectFormDialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) setEditingProject(null);
        }}
        project={editingProject}
        clients={clients}
        allUsers={allUsers}
        onSaved={fetchData}
      />

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="project-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="project-status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {statusOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <FolderKanban className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="projects-total-count">{total}</p>
                <p className="text-xs text-muted-foreground">Total Projects</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                <FolderKanban className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{projects.filter(p => p.status === 'in_progress').length}</p>
                <p className="text-xs text-muted-foreground">In Progress</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <FolderKanban className="h-5 w-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{projects.filter(p => p.status === 'completed').length}</p>
                <p className="text-xs text-muted-foreground">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-green-500/10 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-xl font-bold">{formatCurrency(projects.reduce((sum, p) => sum + (parseFloat(p.budget) || 0), 0))}</p>
                <p className="text-xs text-muted-foreground">Total Budget</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Projects Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : projects.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <FolderKanban className="h-12 w-12 mb-4 opacity-50" />
            <p>No projects found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <Card key={project.id} className="hover:border-primary/50 transition-colors" data-testid={`project-card-${project.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg line-clamp-1 transition-colors" data-testid={`project-name-${project.id}`}>
                      <Link to={`/projects/${project.id}`} className="hover:text-primary hover:underline cursor-pointer">
                        {project.name}
                      </Link>
                    </CardTitle>
                    <p className="text-sm text-muted-foreground"><ClientLink id={project.client_id} name={project.client_name} /></p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" data-testid={`project-actions-${project.id}`}>
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => { setSelectedProject(project); setIsViewDialogOpen(true); }}>
                        <Eye className="h-4 w-4 mr-2" />
                        View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate(`/projects/${project.id}`)}>
                        <FlaskConical className="h-4 w-4 mr-2" />
                        QA Module
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate(`/invoices/new?project=${project.id}`)} data-testid={`raise-invoice-${project.id}`}>
                        <Receipt className="h-4 w-4 mr-2" />
                        Raise Invoice
                      </DropdownMenuItem>
                      {canEdit && (
                      <DropdownMenuItem onClick={() => handleEdit(project)} data-testid={`edit-project-${project.id}`}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      )}
                      {canDelete && (
                      <DropdownMenuItem onClick={() => handleDelete(project)} className="text-destructive focus:text-destructive" data-testid={`delete-project-${project.id}`}>
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {project.description && (
                    <TruncatedText
                      text={project.description}
                      lines={2}
                      className="text-xs text-muted-foreground"
                    />
                  )}
                  <div className="flex gap-2">
                    {getStatusBadge(project.status)}
                    {getPriorityBadge(project.priority)}
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="font-medium">{project.progress_percentage}%</span>
                    </div>
                    <Progress value={project.progress_percentage} className="h-2" />
                  </div>
                  <div className="flex justify-between text-sm">
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      <span>{formatDate(project.end_date) || 'No deadline'}</span>
                    </div>
                    <span className="font-medium text-emerald-500">{formatCurrency(project.budget || 0)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!loading && total > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          testIdPrefix="projects-pagination"
        />
      )}

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Project Details</DialogTitle>
          </DialogHeader>
          {selectedProject && (
            <div className="space-y-4">
              <h3 className="text-xl font-bold">{selectedProject.name}</h3>
              <p className="text-muted-foreground"><ClientLink id={selectedProject.client_id} name={selectedProject.client_name} /></p>
              <div className="flex gap-2">
                {getStatusBadge(selectedProject.status)}
                {getPriorityBadge(selectedProject.priority)}
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Budget</p>
                  <p className="font-medium">{formatCurrency(selectedProject.budget || 0)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Progress</p>
                  <p className="font-medium">{selectedProject.progress_percentage}%</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Start Date</p>
                  <p className="font-medium">{formatDate(selectedProject.start_date) || 'Not set'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">End Date</p>
                  <p className="font-medium">{formatDate(selectedProject.end_date) || 'Not set'}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
